import { Injectable } from '@angular/core';
import { EDeliverableType, EPlatform } from '@enums/campaign.enum';
import { ComponentStore } from '@ngrx/component-store';
import { CreatorPlatformListService } from '@pages/creators/creator-platform-list/creator-platform-list.service';
import { camelCase } from 'lodash';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { IDeliverableConfigurationApi } from './deliverable-list.interface';
import { DeliverableListService } from './deliverable-list.service';

interface IDeliverableConfigurationState {
  error: string | null;
  loading: boolean;
  deliverableConfiguration: IDeliverableConfigurationApi[] | null;
  validPlatforms: EPlatform[] | null;
}

const initialState: IDeliverableConfigurationState = {
  deliverableConfiguration: null,
  error: null,
  loading: false,
  validPlatforms: null,
};

/**
 * Maps every API/form deliverable key to its `EDeliverableType`.
 * Some types have two keys (singular + plural) because the backend and the form use different names.
 * The compile-time assertion below ensures every `EDeliverableType` value is present.
 */
export const DELIVERABLE_KEY_MAP = {
  audioTrackings: EDeliverableType.audioTracking,
  captions: EDeliverableType.caption,
  chatbot: EDeliverableType.chatBot,
  chatbotMulti: EDeliverableType.chatbotMulti,
  chatbotMultis: EDeliverableType.chatbotMulti,
  chatbots: EDeliverableType.chatBot,
  contents: EDeliverableType.content,
  customs: EDeliverableType.custom,
  descriptions: EDeliverableType.description,
  gamePlays: EDeliverableType.gamePlay,
  hashTags: EDeliverableType.hashTag,
  mentions: EDeliverableType.mention,
  messages: EDeliverableType.message,
  productPlacements: EDeliverableType.productPlacement,
  showcases: EDeliverableType.showcase,
  talkingPointAis: EDeliverableType.talkingPointAi,
  talkingPoints: EDeliverableType.talkingPoint,
  textTrackings: EDeliverableType.textTracking,
  titles: EDeliverableType.title,
  visuals: EDeliverableType.visual,
} as const;

/** Build fails if a new `EDeliverableType` value is not present as a value in `DELIVERABLE_KEY_MAP`. */
type TAssertKeyMapCoversAllDeliverableTypes =
  Exclude<EDeliverableType, (typeof DELIVERABLE_KEY_MAP)[keyof typeof DELIVERABLE_KEY_MAP]> extends never
    ? true
    : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-underscore-dangle
const _keyMapCoverage: TAssertKeyMapCoversAllDeliverableTypes = true;

@Injectable()
export class DeliverableConfigurationStore extends ComponentStore<IDeliverableConfigurationState> {
  public readonly configurations$ = this.selectSignal((state) => state.deliverableConfiguration);
  public readonly loading$ = this.selectSignal((state) => state.loading);
  public readonly error$ = this.selectSignal((state) => state.error);
  public readonly validPlatforms$ = this.selectSignal((state) => state.validPlatforms);

  public readonly keyMap: Record<string, EDeliverableType> = DELIVERABLE_KEY_MAP;
  public readonly order = Object.keys(this.keyMap);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchAvailableConfigurations = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(() =>
        this.deliverableListService.getAvailableDeliverableConfigurations().pipe(
          tap((deliverableConfiguration: IDeliverableConfigurationApi[]) => {
            this.patchState({
              deliverableConfiguration: deliverableConfiguration.map((config) => ({
                ...config,
                type: config.deliverable,
              })),
              loading: false,
            });
          }),
          this.handleError('Failed to load deliverable configurations'),
        ),
      ),
    );
  });

  public readonly fetchValidPlatforms = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      switchMap(() =>
        this.creatorPlatformListService.getValidPlatforms().pipe(
          tap((platforms: string[]) => {
            this.patchState({
              validPlatforms: platforms.map((platform) => camelCase(platform) as EPlatform),
            });
          }),
          catchError(() => {
            this.patchState({ validPlatforms: [] });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  constructor(
    private readonly deliverableListService: DeliverableListService,
    private readonly creatorPlatformListService: CreatorPlatformListService,
  ) {
    super(initialState);
  }
}
